import { useDateFormatter, useTranslator } from '@bluebeam/react-i18n';
import { useAuth } from 'auth';
import Button from 'components/Button';
import OptionsMeatball from 'components/Icons/OptionsMeatball';
import BaseTextarea from 'components/inputs/BaseTextarea';
import Label from 'components/inputs/Label';
import ConfirmModal from 'components/modals/ConfirmModal';
import OutsideClick from 'components/OutsideClick';
import AppStoreContext from 'contexts/AppStoreContext';
import NoComments from 'images/comments.svg';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import { Comment, CommentMap } from 'records/Comment';
import { ModalState } from 'records/ModalState';

interface Props {
  comments: CommentMap;
  comment: string;
  setComment: (c: string) => void;
}

const CommentsEmpty: React.FC = () => {
  const { translate } = useTranslator(['mapView']);

  return (
    <div className="items-center grow-1 column justify-center">
      <img src={NoComments} alt="large RFI illustration" />
      <div className="font-size-7 color-gray-7 mb2 pt4">
        {translate('mapView:comments.noComments')}
      </div>
      <div className="font-size-5 color-gray-6 mb6">
        {translate('mapView:comments.beTheFirstComment')}
      </div>
    </div>
  );
};

type CommentCardProps = {
  comment: Comment;
};

type CommentMenuButtonProps = {
  children: React.ReactNode;
  onClick: () => void;
  style: React.CSSProperties;
};

const CommentMenuButton: React.FC<CommentMenuButtonProps> = ({
  children,
  onClick,
  style,
}) => {
  return (
    <Button
      onClick={onClick}
      size="lg"
      className="pl4 pr4"
      style={{
        background: 'none',
        color: '#868686',
        fontSize: '14px',
        outline: 'none',
        ...style,
      }}
      variant="tertiary"
    >
      {children}
    </Button>
  );
};

const CommentMeatballButton: React.FC<{
  onDelete: () => void;
  onEdit: () => void;
}> = ({ onDelete, onEdit }) => {
  const { translate } = useTranslator(['mapView']);
  const [open, setOpen] = useState(false);
  return (
    <Manager>
      <Reference>
        {({ ref }): React.ReactNode => (
          <button
            ref={ref}
            className="pl2 pr0 cursor-pointer tooltip-button b-width-0"
            type="button"
            onClick={() => {
              setOpen(true);
            }}
          >
            <OptionsMeatball
              height="20px"
              width="20px"
              style={{ color: '#373d42' }}
            />
          </button>
        )}
      </Reference>
      <Popper placement="bottom-end">
        {({ ref, style, placement }): React.ReactNode => {
          return open ? (
            <div
              ref={ref}
              style={{
                ...style,
                zIndex: 2,
              }}
              data-placement={placement}
            >
              <OutsideClick
                onOutsideClick={() => {
                  setOpen(false);
                }}
              >
                <div className="column bgd-white-2 font-size-4 center b-3">
                  <div className="bb-3 hrem-2p75">
                    <CommentMenuButton
                      onClick={onEdit}
                      style={{ color: '#00579c' }}
                    >
                      {translate('mapView:comments.editComment')}
                    </CommentMenuButton>
                  </div>
                  <div className="hrem-2p75">
                    <CommentMenuButton
                      onClick={() => {
                        setOpen(false);
                        onDelete();
                      }}
                      style={{ color: '#a71629' }}
                    >
                      {translate('mapView:comments.deleteComment')}
                    </CommentMenuButton>
                  </div>
                </div>
              </OutsideClick>
            </div>
          ) : null;
        }}
      </Popper>
    </Manager>
  );
};

const CommentCardContainer: React.FC<{
  children: React.ReactNode;
  comment: Comment;
}> = ({ children, comment }) => {
  const { translate } = useTranslator(['mapView']);
  const dateFormatter = useDateFormatter({
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  const { date, user } = comment;

  return (
    <div className="mb3 mr4 b-3">
      <div
        className="row justify-between bgd-white-2 p3 bb-solid bb-width-1 bb-light-grey-1"
        style={{
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <div className="truncate" style={{ fontWeight: 400 }}>
          {translate('mapView:comments.fromUser', {
            userEmailAddress: user?.email,
          })}
        </div>
        <div className="color-gray-6 font-size-3">
          {dateFormatter?.format(new Date(date * 1000))}
        </div>
      </div>
      {children}
    </div>
  );
};

const CommentCard: React.FC<CommentCardProps> = ({ comment }) => {
  const { userId } = useAuth();
  const { translate } = useTranslator(['mapView', 'buttons']);
  const { comment: commentText } = comment;
  const { state: appState, actions } = useContext(AppStoreContext);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editText, setEditText] = useState<string>(commentText);

  const handleDelete = useCallback(() => {
    const modalState = new ModalState({
      id: 'ConfirmPunchCommentDelete',
      portal: 'main',
      open: true,
      shouldCloseOnEsc: false,
      shouldCloseOnOverlayClick: false,
      component: ConfirmModal,
      props: {
        id: 'confirm-punch-comment-delete-modal',
        acceptButtonText: translate('buttons:delete'),
        acceptButtonType: 'delete',
        denyButtonText: translate('buttons:cancel'),
        headerText: translate('mapView:comments.deleteComment'),
        content: (
          <>
            <div>{translate('mapView:comments.permanentlyDeleteComment')}</div>
            <div className="medium mt3">
              {translate('mapView:comments.areYouSureDeleteComment')}
            </div>
          </>
        ),
        acceptCallback: (): void => {
          actions.deleteComment(
            comment.id,
            comment.issueid,
            comment.projectid,
            Math.floor(Date.now() / 1000)
          );
          actions.clearAllModals();
        },
        denyCallback: (): void => {
          actions.clearAllModals();
        },
      },
    });

    actions.createModal(modalState);
  }, [comment.id, comment.issueid, comment.projectid]);

  const handleEdit = () => {
    actions.editComment(
      comment.issueid,
      comment.projectid,
      comment.id,
      editText.trim(),
      Math.floor(Date.now() / 1000)
    );
    setEditMode(false);
  };

  const handleCancelEdit = () => {
    setEditText(commentText);
    setEditMode(false);
  };

  const canModifyComment = userId === comment.user.id;

  const dateFormatter = useDateFormatter({
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <>
      {editMode ? (
        <>
          <div className="row justify-between mr4 mb3">
            <Button variant="secondary" onClick={handleCancelEdit}>
              {translate('buttons:cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleEdit}
              disabled={editText.length === 0}
            >
              {translate('buttons:saveChanges')}
            </Button>
          </div>
          <CommentCardContainer comment={comment}>
            <div className="flex">
              <BaseTextarea
                id="update-punch-overlay-comment-textarea"
                className="w100 h100"
                style={{
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  borderWidth: 0,
                }}
                rows={5}
                value={editText}
                onChange={({ currentTarget: { value } }) => setEditText(value)}
              />
            </div>
          </CommentCardContainer>
        </>
      ) : (
        <CommentCardContainer comment={comment}>
          <div className="pt2 pl3 pr3 pb3">
            <div className="row justify-between">
              <div className="pb2">
                <div style={{ fontWeight: 400 }}>
                  {translate('mapView:comments.comment')}
                  {comment.clientmodifiedtimestamp > 0 && (
                    <p
                      className="inline color-gray-6 font-size-1 ml1"
                      style={{ fontWeight: 300 }}
                    >
                      {translate('mapView:comments.edited', {
                        dateTime: dateFormatter?.format(
                          new Date(comment.clientmodifiedtimestamp * 1000)
                        ),
                      })}
                    </p>
                  )}
                </div>
              </div>
              {canModifyComment && (
                <CommentMeatballButton
                  onDelete={handleDelete}
                  onEdit={() => {
                    setEditText(commentText);
                    setEditMode(true);
                  }}
                />
              )}
            </div>
            <p className="m0" style={{ whiteSpace: 'pre-line' }}>
              {`${commentText}`}
            </p>
          </div>
        </CommentCardContainer>
      )}
    </>
  );
};

type CommentCardListProps = {
  comments: CommentMap;
};
const CommentCardList: React.FC<CommentCardListProps> = ({ comments }) => {
  const commentsEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    if (commentsEndRef.current !== null) {
      commentsEndRef.current?.scrollIntoView();
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [comments.size]);

  return (
    <div className="grow-1 column-reverse oa pt3 light">
      <div ref={commentsEndRef} />
      {comments.valueSeq().map((c) => (
        <CommentCard key={c.id} comment={c} />
      ))}
      <div className="pt3" />
    </div>
  );
};

type CommentInputProps = {
  comment: string;
  setComment: (c: string) => void;
};
const CommentInput: React.FC<CommentInputProps> = ({ comment, setComment }) => {
  const { translate } = useTranslator(['mapView']);
  return (
    <>
      <Label label={translate('mapView:comments.comment')} />
      <BaseTextarea
        id="update-punch-overlay-comment-textarea"
        className="w100"
        rows={4}
        value={comment}
        onChange={({ currentTarget: { value } }) => setComment(value)}
      />
    </>
  );
};

const Comments: React.FC<Props> = ({ comments, comment, setComment }) => {
  return (
    <div className="column-reverse grow-1 ml4 mb3 oh">
      <div className="justify-end pt3 bt-light-grey-1 bt-width-1 bt-solid mr4">
        <CommentInput comment={comment} setComment={setComment} />
      </div>
      {comments.size === 0 ? (
        <CommentsEmpty />
      ) : (
        <CommentCardList comments={comments} />
      )}
    </div>
  );
};

export default Comments;
